import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import api from '../services/api';
import { getHolidayAllowance, upsertHolidayAllowance } from '../services/HolidayAllowanceService';
import { getAllUsers, setOvertimeAutoApprove } from '../services/UserService';
import { getCurrentUserRole, getCurrentUserId } from '../utils/authHelper';
import { useToast } from '../context/UIContext';
import { extractApiError } from '../utils/apiError';
import { formatDate } from '../utils/dateConverter';
import '../styles/EmployeeDetail.css';

const THIS_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [THIS_YEAR, THIS_YEAR - 1, THIS_YEAR - 2];
const MONTHS = [
    'Januar','Februar','März','April','Mai','Juni',
    'Juli','August','September','Oktober','November','Dezember',
];

function fmtSecs(secs) {
    if (secs === null || secs === undefined) return '–';
    const abs = Math.abs(secs);
    const h   = Math.floor(abs / 3600);
    const m   = Math.floor((abs % 3600) / 60);
    return `${h}h ${String(m).padStart(2, '0')}min`;
}

function fmtOtSecs(secs) {
    if (secs === null || secs === undefined) return '–';
    if (secs === 0) return '0h';
    const sign = secs < 0 ? '−' : '+';
    const abs  = Math.abs(secs);
    const h    = Math.floor(abs / 3600);
    const m    = Math.floor((abs % 3600) / 60);
    return `${sign}${h}h ${String(m).padStart(2, '0')}min`;
}

// Net monthly balance = (Ist − Soll) + manual correction, in seconds — what the
// account shows as "Überstunden", so a month worked short shows a negative value.
function otDelta(a) {
    return (a.overtime || 0) + (a.manual_correction || 0);
}

//: How many weeks are shown before the "alle anzeigen" toggle. A year reaches ~52 rows
//  and the recent ones are the ones being checked.
const WEEK_PREVIEW = 12;

function fmtOtMins(mins) {
    if (mins === null || mins === undefined) return '–';
    if (mins === 0) return '0h';
    const sign = mins < 0 ? '−' : '+';
    const abs  = Math.abs(mins);
    const h    = Math.floor(abs / 60);
    const m    = abs % 60;
    return m === 0 ? `${sign}${h}h` : `${sign}${h}h ${String(m).padStart(2, '0')}min`;
}

// The bar is a proportion of a 10 h deviation, so an ordinary week stays readable and an
// outlier simply saturates instead of flattening every other row.
function barWidth(overtimeMinutes) {
    const pct = (Math.abs(overtimeMinutes || 0) / 600) * 100;
    return Math.min(100, Math.round(pct));
}

function fmtMins(mins) {
    if (!mins && mins !== 0) return '–';
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m === 0 ? `${h}h` : `${h}h ${String(m).padStart(2, '0')}min`;
}

export default function EmployeeDetail() {
    const { userId }   = useParams();
    const navigate     = useNavigate();
    const location     = useLocation();
    const toast        = useToast();
    const info         = location.state || {};
    const name         = info.name     || 'Mitarbeiter';
    const username     = info.username || '';

    const [year, setYear] = useState(THIS_YEAR);

    const [otAccounts, setOtAccounts]     = useState([]);
    const [entries, setEntries]           = useState([]);
    const [allocData, setAllocData]       = useState({ allocations: [], by_project: [] });
    const [holidays, setHolidays]         = useState([]);
    const [sickDays, setSickDays]         = useState([]);
    const [otApprovals, setOtApprovals]   = useState([]);
    const [weekly, setWeekly]             = useState([]);
    const [loadingWeekly, setLoadingWeekly] = useState(true);
    const [showAllWeeks, setShowAllWeeks] = useState(false);
    const [loadingOtApprovals, setLoadingOtApprovals] = useState(true);
    const [autoApprove, setAutoApprove] = useState(null);
    const [savingAutoApprove, setSavingAutoApprove] = useState(false);

    const [allowance, setAllowance]               = useState(null);
    const [loadingAllowance, setLoadingAllowance] = useState(false);
    const [allowanceEdit, setAllowanceEdit]       = useState(false);
    const [editBase, setEditBase]                 = useState('');
    const [editExtra, setEditExtra]               = useState('');
    const [editReason, setEditReason]             = useState('');
    const [savingAllowance, setSavingAllowance]   = useState(false);

    const [loadingOt, setLoadingOt]   = useState(true);
    const [loadingEn, setLoadingEn]   = useState(true);
    const [loadingAl, setLoadingAl]   = useState(true);
    const [loadingAbs, setLoadingAbs] = useState(true);

    const [expandedRecordId, setExpandedRecordId] = useState(null);

    const role = getCurrentUserRole();
    const currentUserId = getCurrentUserId();
    const isAdmin = role === 'admin' || role === 'super_admin';
    // An employee reaches this page for their own id (canView below), so acting on it is
    // gated separately from looking at it.
    const canView = isAdmin || userId === currentUserId;

    // All years in one request; the year filter is applied client-side.
    useEffect(() => {
        if (!canView) return;
        let cancelled = false;
        setLoadingOt(true);
        api.get(`/OvertimeAccount/user/${userId}`)
            .then(r => { if (!cancelled) { setOtAccounts(r.data || []); setLoadingOt(false); } })
            .catch(() => { if (!cancelled) { setOtAccounts([]); setLoadingOt(false); } });
        return () => { cancelled = true; };
    }, [userId, canView]);

    useEffect(() => {
        if (!canView) return;
        let cancelled = false;
        setLoadingOtApprovals(true);
        api.get(`/OvertimeApproval/user/${userId}`)
            .then(r => { if (!cancelled) { setOtApprovals(r.data || []); setLoadingOtApprovals(false); } })
            .catch(() => { if (!cancelled) { setOtApprovals([]); setLoadingOtApprovals(false); } });
        return () => { cancelled = true; };
    }, [userId, canView]);

    // There is no GET /User/{id}; the org-scoped list carries the flag.
    useEffect(() => {
        if (!isAdmin) return undefined;
        let cancelled = false;
        getAllUsers()
            .then(list => {
                if (cancelled) return;
                const me = (list || []).find(u => String(u.id) === String(userId));
                setAutoApprove(me ? !!me.overtime_auto_approve : false);
            })
            .catch(() => { if (!cancelled) setAutoApprove(false); });
        return () => { cancelled = true; };
    }, [userId, isAdmin]);

    const toggleAutoApprove = async () => {
        const next = !autoApprove;
        setSavingAutoApprove(true);
        try {
            const res = await setOvertimeAutoApprove(userId, next);
            setAutoApprove(next);
            const n = res?.approved_days || 0;
            toast(next
                ? `Dauerhaft OK aktiviert${n ? ` — ${n} offene Tag(e) genehmigt` : ''}.`
                : 'Dauerhaft OK deaktiviert. Bereits genehmigte Tage bleiben genehmigt.');
            // Enabling clears the backlog server-side, so the list and the balances moved.
            const [approvals, accounts] = await Promise.all([
                api.get(`/OvertimeApproval/user/${userId}`),
                api.get(`/OvertimeAccount/user/${userId}`),
            ]);
            setOtApprovals(approvals.data || []);
            setOtAccounts(accounts.data || []);
        } catch (e) {
            toast(extractApiError(e, 'Dauerhaft OK konnte nicht geändert werden.'));
        } finally {
            setSavingAutoApprove(false);
        }
    };

    // Newest week first, like the Einträge table on this page.
    const weeksNewestFirst = useMemo(
        () => [...weekly].sort((a, b) => (a.week_start < b.week_start ? 1 : -1)),
        [weekly],
    );
    const visibleWeeks = showAllWeeks ? weeksNewestFirst : weeksNewestFirst.slice(0, WEEK_PREVIEW);

    useEffect(() => {
        let cancelled = false;
        setLoadingWeekly(true);
        api.get(`/Stats/admin/employee/${userId}/weekly?year=${year}`)
            .then(res => { if (!cancelled) setWeekly(res.data || []); })
            .catch(() => { if (!cancelled) setWeekly([]); })
            .finally(() => { if (!cancelled) setLoadingWeekly(false); });
        return () => { cancelled = true; };
    }, [userId, year]);

    useEffect(() => {
        if (!canView) return;
        let cancelled = false;
        setLoadingEn(true);
        setLoadingAl(true);
        setExpandedRecordId(null);

        api.get(`/Stats/admin/employee/${userId}/records?year=${year}`)
            .then(r => { if (!cancelled) { setEntries(r.data || []); setLoadingEn(false); } })
            .catch(() => { if (!cancelled) { setEntries([]); setLoadingEn(false); } });

        api.get(`/Stats/admin/employee/${userId}/allocations?year=${year}`)
            .then(r => { if (!cancelled) { setAllocData(r.data || { allocations: [], by_project: [] }); setLoadingAl(false); } })
            .catch(() => { if (!cancelled) { setAllocData({ allocations: [], by_project: [] }); setLoadingAl(false); } });

        return () => { cancelled = true; };
    }, [year, userId, canView]);

    useEffect(() => {
        if (!canView) return;
        let cancelled = false;
        setLoadingAbs(true);
        Promise.all([
            api.get(`/Holiday/user/${userId}`),
            api.get(`/SickDay/user/${userId}`),
        ])
            .then(([hRes, sRes]) => {
                if (!cancelled) {
                    setHolidays(hRes.data || []);
                    setSickDays(sRes.data || []);
                }
            })
            .catch(() => { if (!cancelled) { setHolidays([]); setSickDays([]); } })
            .finally(() => { if (!cancelled) setLoadingAbs(false); });
        return () => { cancelled = true; };
    }, [userId, canView]);

    useEffect(() => {
        if (!canView) return;
        let cancelled = false;
        setAllowanceEdit(false);
        setLoadingAllowance(true);
        api.get(`/HolidayAllowance/user/${userId}/year/${year}`)
            .then(res => { if (!cancelled) setAllowance(res.data); })
            .catch(() => { if (!cancelled) setAllowance(null); })
            .finally(() => { if (!cancelled) setLoadingAllowance(false); });
        return () => { cancelled = true; };
    }, [userId, year, canView]);

    const allocByRecord = useMemo(() => {
        const map = {};
        for (const a of (allocData.allocations || [])) {
            const key = a.daily_work_record_id;
            if (!map[key]) map[key] = [];
            map[key].push(a);
        }
        return map;
    }, [allocData.allocations]);

    const otByRecord = useMemo(() => {
        const map = {};
        for (const a of otApprovals) {
            if (a.daily_work_record_id) map[a.daily_work_record_id] = a;
        }
        return map;
    }, [otApprovals]);

    const otLabel = { approved: 'Genehmigt', rejected: 'Abgelehnt', pending: 'Ausstehend' };

    const carryover = otAccounts
        .filter(a => a.year < year)
        .reduce((s, a) => s + otDelta(a), 0);

    const totalAllYears = otAccounts.reduce((s, a) => s + otDelta(a), 0);

    const yearAccounts = otAccounts
        .filter(a => a.year === year)
        .sort((a, b) => a.month - b.month);

    let running = carryover;
    const accountsWithRunning = yearAccounts.map(a => {
        running += otDelta(a);
        return { ...a, _running: running };
    });

    const totalPlanned  = yearAccounts.reduce((s, a) => s + (a.planned_time || 0), 0);
    const totalActual   = yearAccounts.reduce((s, a) => s + (a.actual_time  || 0), 0);
    const totalOvertime = yearAccounts.reduce((s, a) => s + otDelta(a), 0);

    const openCount   = entries.filter(r => r.status === 'open').length;
    const closedCount = entries.filter(r => r.status === 'closed').length;
    const byProject   = allocData.by_project || [];
    const totalAllocMins = byProject.reduce((s, p) => s + p.total_minutes, 0);

    const yearHolidays = holidays.filter(h => h.start_date && h.start_date.startsWith(String(year)));
    const yearSickDays = sickDays.filter(s => s.start_date && s.start_date.startsWith(String(year)));
    const totalHolidayDays = yearHolidays.reduce((s, h) => s + (h.number_days || 0), 0);
    const totalSickDays    = yearSickDays.reduce((s, d) => s + (d.number_days || 0), 0);

    const yearOtApprovals = otApprovals
        .filter(a => a.date && String(a.date).startsWith(String(year)))
        .sort((a, b) => (a.date || '').localeCompare(b.date || ''));
    const approvedOtCount = yearOtApprovals.filter(a => a.approval_status === 'approved').length;
    const pendingOtCount  = yearOtApprovals.filter(a => a.approval_status === 'pending').length;
    const rejectedOtCount = yearOtApprovals.filter(a => a.approval_status === 'rejected').length;

    useEffect(() => {
        if (!canView) {
            navigate(`/employee-hours/${currentUserId}`, { replace: true });
        }
    }, [canView, userId, currentUserId, navigate]);

    const handleSaveAllowance = () => {
        setSavingAllowance(true);
        upsertHolidayAllowance(userId, year, {
            base_allowance: parseInt(editBase, 10) || 0,
            extra_days: parseInt(editExtra, 10) || 0,
            extra_reason: editReason || null,
        })
            .then(updated => {
                setAllowance(updated);
                setAllowanceEdit(false);
            })
            .catch(err => {
                toast(extractApiError(err, 'Fehler beim Speichern des Urlaubskontingents.'));
            })
            .finally(() => setSavingAllowance(false));
    };

    return (
        <div className="ed-container">

            <div className="ed-header">
                <button className="ed-back-btn" onClick={() => navigate(-1)}>
                    ← Zurück
                </button>
                <div className="ed-title-block">
                    <h2 className="ed-title">{name}</h2>
                    {username && <span className="ed-subtitle">@{username}</span>}
                </div>
                {/* Admins only: /timetracking/:userId is behind AdminRoute, so for an
                    employee this would be a button that bounces them to /kpi. */}
                {isAdmin && (
                    <Link className="ed-edit-times-btn" to={`/timetracking/${userId}`}>
                        Zeiten bearbeiten
                    </Link>
                )}
                <select
                    className="ed-year-select"
                    value={year}
                    onChange={e => setYear(Number(e.target.value))}
                >
                    {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
            </div>

            <div className="ed-summary-strip">
                <div className="ed-summary-item">
                    <span className="ed-summary-label">Soll {year}</span>
                    <span className="ed-summary-value">{loadingOt ? '…' : fmtSecs(totalPlanned)}</span>
                </div>
                <div className="ed-summary-divider" />
                <div className="ed-summary-item">
                    <span className="ed-summary-label">Ist {year}</span>
                    <span className="ed-summary-value">{loadingOt ? '…' : fmtSecs(totalActual)}</span>
                </div>
                <div className="ed-summary-divider" />
                <div className="ed-summary-item">
                    <span className="ed-summary-label">Überstunden {year}</span>
                    <span className={`ed-summary-value ed-ot ${totalOvertime > 0 ? 'ed-ot--pos' : totalOvertime < 0 ? 'ed-ot--neg' : ''}`}>
                        {loadingOt ? '…' : fmtOtSecs(totalOvertime)}
                    </span>
                </div>
                <div className="ed-summary-divider" />
                <div className="ed-summary-item">
                    <span className="ed-summary-label">Gesamtsaldo</span>
                    <span className={`ed-summary-value ed-ot ${totalAllYears > 0 ? 'ed-ot--pos' : totalAllYears < 0 ? 'ed-ot--neg' : ''}`}>
                        {loadingOt ? '…' : fmtOtSecs(totalAllYears)}
                    </span>
                </div>
                <div className="ed-summary-divider" />
                <div className="ed-summary-item">
                    <span className="ed-summary-label">Buchungen</span>
                    <span className="ed-summary-value">{loadingAl ? '…' : allocData.allocations.length}</span>
                </div>
                <div className="ed-summary-divider" />
                <div className="ed-summary-item">
                    <span className="ed-summary-label">Einträge offen</span>
                    <span className={`ed-summary-value ${openCount > 0 ? 'ed-open-count' : ''}`}>
                        {loadingEn ? '…' : openCount}
                    </span>
                </div>
                <div className="ed-summary-divider" />
                <div className="ed-summary-item">
                    <span className="ed-summary-label">Abgeschlossen</span>
                    <span className="ed-summary-value">{loadingEn ? '…' : closedCount}</span>
                </div>
                <div className="ed-summary-divider" />
                <div className="ed-summary-item">
                    <span className="ed-summary-label">Urlaub genommen {year}</span>
                    <span className="ed-summary-value">{loadingAbs ? '…' : totalHolidayDays}</span>
                </div>
                <div className="ed-summary-divider" />
                <div className="ed-summary-item">
                    <span className="ed-summary-label">Resturlaub {year}</span>
                    <span className={`ed-summary-value ${allowance && allowance.remaining < 0 ? 'ed-sick-count' : ''}`}>
                        {loadingAllowance ? '…' : allowance ? `${allowance.remaining} Tage` : '–'}
                    </span>
                </div>
                <div className="ed-summary-divider" />
                <div className="ed-summary-item">
                    <span className="ed-summary-label">Krankheitstage {year}</span>
                    <span className={`ed-summary-value ${totalSickDays > 0 ? 'ed-sick-count' : ''}`}>
                        {loadingAbs ? '…' : totalSickDays}
                    </span>
                </div>
            </div>

            <div className="ed-two-col">

                <section className="ed-section">
                    <h3 className="ed-section-title">Überstundenkonto</h3>
                    {loadingOt ? (
                        <p className="ed-loading">Lade Überstunden…</p>
                    ) : yearAccounts.length === 0 ? (
                        <p className="ed-empty">Keine Überstundenkonten für {year}.</p>
                    ) : (
                        <div className="ed-card">
                            <table className="ed-table">
                                <thead>
                                    <tr>
                                        <th>Monat</th>
                                        <th className="ed-th-r">Soll</th>
                                        <th className="ed-th-r">Ist</th>
                                        <th className="ed-th-r">Überstunden</th>
                                        <th className="ed-th-r">Laufend</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {carryover !== 0 && (
                                        <tr className="ed-carryover-row">
                                            <td className="ed-month-cell ed-muted">Übertrag {year - 1}</td>
                                            <td className="ed-td-r ed-muted">–</td>
                                            <td className="ed-td-r ed-muted">–</td>
                                            <td className="ed-td-r ed-muted">–</td>
                                            <td className="ed-td-r">
                                                <span className={`ed-ot-chip ${carryover > 0 ? 'ed-ot--pos' : carryover < 0 ? 'ed-ot--neg' : 'ed-ot--zero'}`}>
                                                    {fmtOtSecs(carryover)}
                                                </span>
                                            </td>
                                        </tr>
                                    )}
                                    {accountsWithRunning.map(a => {
                                        const ot  = otDelta(a);
                                        const run = a._running;
                                        return (
                                            <tr key={a.id || `${a.year}-${a.month}`}>
                                                <td className="ed-month-cell">{MONTHS[a.month - 1]}</td>
                                                <td className="ed-td-r">{fmtSecs(a.planned_time)}</td>
                                                <td className="ed-td-r">{fmtSecs(a.actual_time)}</td>
                                                <td className="ed-td-r">
                                                    <span className={`ed-ot-val ${ot > 0 ? 'ed-ot--pos' : ot < 0 ? 'ed-ot--neg' : 'ed-ot--zero'}`}>
                                                        {fmtOtSecs(ot)}
                                                    </span>
                                                </td>
                                                <td className="ed-td-r">
                                                    <span className={`ed-ot-chip ${run > 0 ? 'ed-ot--pos' : run < 0 ? 'ed-ot--neg' : 'ed-ot--zero'}`}>
                                                        {fmtOtSecs(run)}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot>
                                    <tr className="ed-tfoot-row">
                                        <td><strong>Gesamt {year}</strong></td>
                                        <td className="ed-td-r"><strong>{fmtSecs(totalPlanned)}</strong></td>
                                        <td className="ed-td-r"><strong>{fmtSecs(totalActual)}</strong></td>
                                        <td className="ed-td-r">
                                            <strong className={`ed-ot-val ${totalOvertime > 0 ? 'ed-ot--pos' : totalOvertime < 0 ? 'ed-ot--neg' : 'ed-ot--zero'}`}>
                                                {fmtOtSecs(totalOvertime)}
                                            </strong>
                                        </td>
                                        <td className="ed-td-r">
                                            <strong className={`ed-ot-chip ${(carryover + totalOvertime) > 0 ? 'ed-ot--pos' : (carryover + totalOvertime) < 0 ? 'ed-ot--neg' : 'ed-ot--zero'}`}>
                                                {fmtOtSecs(carryover + totalOvertime)}
                                            </strong>
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </section>

                <section className="ed-section">
                    <h3 className="ed-section-title">Projektübersicht</h3>
                    {loadingAl ? (
                        <p className="ed-loading">Lade Projekte…</p>
                    ) : byProject.length === 0 ? (
                        <p className="ed-empty">Keine Buchungen für {year}.</p>
                    ) : (
                        <div className="ed-card">
                            <div className="ed-project-total-row">
                                <span className="ed-project-total-label">Gesamt verbucht</span>
                                <span className="ed-project-total-value">{fmtMins(totalAllocMins)}</span>
                            </div>
                            <div className="ed-project-list">
                                {byProject.map(p => (
                                    <div key={p.project_id} className="ed-project-row">
                                        <div className="ed-project-info">
                                            <span className="ed-project-name">{p.project_name}</span>
                                            <span className="ed-project-meta">
                                                {p.total_hours} &middot; {p.booking_count} {p.booking_count === 1 ? 'Buchung' : 'Buchungen'}
                                            </span>
                                        </div>
                                        <div className="ed-project-bar-wrap">
                                            <div
                                                className="ed-project-bar-fill"
                                                style={{ width: `${p.percent}%` }}
                                            />
                                        </div>
                                        <span className="ed-project-pct">{p.percent}%</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </section>

            </div>

            <section className="ed-section">
                <div className="ed-section-header">
                    <h3 className="ed-section-title">Wochenübersicht {year}</h3>
                    {!loadingWeekly && weekly.length > WEEK_PREVIEW && (
                        <button className="ed-link-btn" onClick={() => setShowAllWeeks(v => !v)}>
                            {showAllWeeks ? 'Weniger anzeigen' : `Alle ${weekly.length} Wochen anzeigen`}
                        </button>
                    )}
                </div>
                {loadingWeekly ? (
                    <p className="ed-loading">Lade Wochenübersicht…</p>
                ) : weekly.length === 0 ? (
                    <p className="ed-empty">Keine Wochendaten für {year}.</p>
                ) : (
                    <div className="ed-card">
                        <table className="ed-table ed-week-table">
                            <thead>
                                <tr>
                                    <th>KW</th>
                                    <th>Zeitraum</th>
                                    <th className="ed-th-r">Ist</th>
                                    <th className="ed-th-r">Soll</th>
                                    <th className="ed-th-r">Differenz</th>
                                    <th>Verlauf</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleWeeks.map(w => (
                                    <tr key={`${w.iso_year}-${w.iso_week}`}>
                                        <td className="ed-week-cell">
                                            KW&nbsp;{w.iso_week}
                                            {w.partial && <span className="ed-week-flag" title="Laufende Woche — Soll nur bis heute">laufend</span>}
                                        </td>
                                        <td className="ed-muted">
                                            {formatDate(w.week_start)} – {formatDate(w.week_end)}
                                            {w.absence_days > 0 && (
                                                <span className="ed-week-flag" title="Feiertag, Urlaub oder Krankheit">
                                                    {w.absence_days} Abw.
                                                </span>
                                            )}
                                        </td>
                                        <td className="ed-td-r">
                                            {fmtMins(w.credited_minutes)}
                                            {w.uncredited_minutes > 0 && (
                                                <span className="ed-week-sub" title="Noch nicht genehmigte Überstunden zählen nicht">
                                                    {fmtMins(w.worked_minutes)} erfasst
                                                </span>
                                            )}
                                        </td>
                                        <td className="ed-td-r ed-muted">{fmtMins(w.expected_minutes)}</td>
                                        <td className={`ed-td-r ${w.overtime_minutes < 0 ? 'ed-sick-count' : ''}`}>
                                            {fmtOtMins(w.overtime_minutes)}
                                        </td>
                                        <td>
                                            <span className="ed-week-bar" aria-hidden="true">
                                                <span
                                                    className={`ed-week-bar-fill${w.overtime_minutes < 0 ? ' ed-week-bar-fill--short' : ''}`}
                                                    style={{ width: `${barWidth(w.overtime_minutes)}%` }}
                                                />
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            <section className="ed-section">
                <div className="ed-section-header">
                    <h3 className="ed-section-title">Überstunden-Genehmigungen {year}</h3>
                    {isAdmin && autoApprove !== null && (
                        <label className="ed-auto-approve" title="Überstunden dieser Person werden sofort genehmigt — keine Freigabe pro Tag.">
                            <input
                                type="checkbox"
                                checked={autoApprove}
                                disabled={savingAutoApprove}
                                onChange={toggleAutoApprove}
                            />
                            <span>Dauerhaft OK</span>
                        </label>
                    )}
                    {!loadingOtApprovals && yearOtApprovals.length > 0 && (
                        <div className="ed-entries-meta">
                            <span className="ed-badge ed-badge--closed">{approvedOtCount} genehmigt</span>
                            {pendingOtCount > 0 && (
                                <span className="ed-badge ed-badge--open">{pendingOtCount} offen</span>
                            )}
                            {rejectedOtCount > 0 && (
                                <span className="ed-badge ed-badge--rejected">{rejectedOtCount} abgelehnt</span>
                            )}
                        </div>
                    )}
                </div>
                {loadingOtApprovals ? (
                    <p className="ed-loading">Lade Überstunden…</p>
                ) : yearOtApprovals.length === 0 ? (
                    <p className="ed-empty">Keine erfassten Überstunden für {year}.</p>
                ) : (
                    <div className="ed-card">
                        <div className="ed-scroll-wrap">
                            <table className="ed-table">
                                <thead>
                                    <tr>
                                        <th>Datum</th>
                                        <th className="ed-th-r">Soll</th>
                                        <th className="ed-th-r">Ist</th>
                                        <th className="ed-th-r">Überstunden</th>
                                        <th>Status</th>
                                        <th>Grund der Ablehnung</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {yearOtApprovals.map(a => (
                                        <tr key={a.id}>
                                            <td className="ed-date-cell">{a.date}</td>
                                            <td className="ed-td-r">{fmtMins(a.planned_minutes)}</td>
                                            <td className="ed-td-r">{fmtMins(a.actual_minutes)}</td>
                                            <td className="ed-td-r">
                                                <span className="ed-ot-val ed-ot--pos">+{fmtMins(a.overtime_minutes)}</span>
                                            </td>
                                            <td>
                                                <span className={`ed-appr-badge ed-appr-badge--${a.approval_status}`}>
                                                    {a.approval_status === 'approved' ? 'Genehmigt'
                                                        : a.approval_status === 'rejected' ? 'Abgelehnt'
                                                            : 'Ausstehend'}
                                                </span>
                                            </td>
                                            <td className="ed-muted">
                                                {a.approval_status === 'rejected' ? (a.rejection_reason || '—') : '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </section>

            <div className="ed-two-col">

                <section className="ed-section">
                    <div className="ed-section-header">
                        <h3 className="ed-section-title">Urlaub {year}</h3>
                        {role === 'admin' && !allowanceEdit && (
                            <button
                                className="ed-back-btn"
                                onClick={() => {
                                    setEditBase(String(allowance?.base_allowance ?? 30));
                                    setEditExtra(String(allowance?.extra_days ?? 0));
                                    setEditReason(allowance?.extra_reason ?? '');
                                    setAllowanceEdit(true);
                                }}
                            >
                                Kontingent bearbeiten
                            </button>
                        )}
                    </div>

                    <div className="ed-card" style={{ marginBottom: '1rem' }}>
                        {loadingAllowance ? (
                            <p className="ed-loading" style={{ padding: 'var(--space-4) var(--space-6)', margin: 0 }}>Lade Kontingent…</p>
                        ) : allowanceEdit ? (
                            <div style={{ padding: 'var(--space-4) var(--space-6)' }}>
                                <p style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-secondary)' }}>
                                    Urlaubskontingent {year} bearbeiten
                                </p>
                                <div style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                    <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
                                        <span className="ed-summary-label">Grundkontingent (Tage)</span>
                                        <input
                                            type="number"
                                            min="0"
                                            value={editBase}
                                            onChange={e => setEditBase(e.target.value)}
                                            style={{ width: '7rem', padding: 'var(--space-2) var(--space-3)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', textAlign: 'right', background: 'var(--color-surface)' }}
                                        />
                                    </label>
                                    <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
                                        <span className="ed-summary-label">Extratage</span>
                                        <input
                                            type="number"
                                            min="0"
                                            value={editExtra}
                                            onChange={e => setEditExtra(e.target.value)}
                                            style={{ width: '7rem', padding: 'var(--space-2) var(--space-3)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', textAlign: 'right', background: 'var(--color-surface)' }}
                                        />
                                    </label>
                                    <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)', flex: 1, minWidth: '14rem' }}>
                                        <span className="ed-summary-label">Grund für Extratage</span>
                                        <input
                                            type="text"
                                            value={editReason}
                                            onChange={e => setEditReason(e.target.value)}
                                            placeholder="z.B. Todesfall in der Familie"
                                            style={{ padding: 'var(--space-2) var(--space-3)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', background: 'var(--color-surface)', width: '100%', boxSizing: 'border-box' }}
                                        />
                                    </label>
                                    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                                        <button
                                            className="ed-back-btn"
                                            onClick={() => setAllowanceEdit(false)}
                                            disabled={savingAllowance}
                                        >
                                            Abbrechen
                                        </button>
                                        <button
                                            className="ed-back-btn"
                                            onClick={handleSaveAllowance}
                                            disabled={savingAllowance}
                                            style={{ background: 'var(--color-primary)', color: '#fff', border: 'none', opacity: savingAllowance ? 0.7 : 1 }}
                                        >
                                            {savingAllowance ? 'Speichern…' : 'Speichern'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div style={{ padding: 'var(--space-4) var(--space-6)', display: 'flex', alignItems: 'center', gap: 'var(--space-5)', flexWrap: 'wrap' }}>
                                <div className="ed-summary-item">
                                    <span className="ed-summary-label">Grundkontingent</span>
                                    <span className="ed-summary-value">{allowance ? allowance.base_allowance : '–'}</span>
                                </div>
                                <div className="ed-summary-item">
                                    <span className="ed-summary-label">+ Übertrag</span>
                                    <span className="ed-summary-value">{allowance ? allowance.carry_over : '–'}</span>
                                </div>
                                <div className="ed-summary-item" style={{ flex: 1 }}>
                                    <span className="ed-summary-label">
                                        + Extratage{allowance?.extra_reason ? ` · ${allowance.extra_reason}` : ''}
                                    </span>
                                    <span className="ed-summary-value">{allowance ? allowance.extra_days : '–'}</span>
                                </div>
                                <div className="ed-summary-divider" />
                                <div className="ed-summary-item">
                                    <span className="ed-summary-label">Gesamt verfügbar</span>
                                    <span className="ed-summary-value">{allowance ? allowance.total_available : '–'}</span>
                                </div>
                                <div className="ed-summary-divider" />
                                <div className="ed-summary-item">
                                    <span className="ed-summary-label">Genommen</span>
                                    <span className="ed-summary-value">{allowance ? allowance.used : '–'}</span>
                                </div>
                                <div className="ed-summary-item">
                                    <span className="ed-summary-label">Resturlaub</span>
                                    <span
                                        className={`ed-summary-value ${allowance && allowance.remaining < 0 ? 'ed-sick-count' : ''}`}
                                        style={allowance && allowance.remaining >= 0 ? { color: 'var(--color-primary)' } : undefined}
                                    >
                                        {allowance ? `${allowance.remaining} Tage` : '–'}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    {loadingAbs ? (
                        <p className="ed-loading">Lade Urlaubseinträge…</p>
                    ) : yearHolidays.length === 0 ? (
                        <p className="ed-empty">Keine Urlaubseinträge für {year}.</p>
                    ) : (
                        <div className="ed-card">
                            <table className="ed-table">
                                <thead>
                                    <tr>
                                        <th>Von</th>
                                        <th>Bis</th>
                                        <th className="ed-th-r">Tage</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {yearHolidays
                                        .sort((a, b) => (a.start_date || '').localeCompare(b.start_date || ''))
                                        .map(h => (
                                            <tr key={h.id}>
                                                <td>{h.start_date || '–'}</td>
                                                <td>{h.end_date || '–'}</td>
                                                <td className="ed-td-r"><strong>{h.number_days ?? '–'}</strong></td>
                                            </tr>
                                        ))
                                    }
                                </tbody>
                                <tfoot>
                                    <tr className="ed-tfoot-row">
                                        <td colSpan={2}><strong>Gesamt</strong></td>
                                        <td className="ed-td-r"><strong>{totalHolidayDays} Tage</strong></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </section>

                <section className="ed-section">
                    <h3 className="ed-section-title">Krankmeldungen {year}</h3>
                    {loadingAbs ? (
                        <p className="ed-loading">Lade Krankmeldungen…</p>
                    ) : yearSickDays.length === 0 ? (
                        <p className="ed-empty">Keine Krankmeldungen für {year}.</p>
                    ) : (
                        <div className="ed-card">
                            <table className="ed-table">
                                <thead>
                                    <tr>
                                        <th>Von</th>
                                        <th>Bis</th>
                                        <th className="ed-th-r">Tage</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {yearSickDays
                                        .sort((a, b) => (a.start_date || '').localeCompare(b.start_date || ''))
                                        .map(s => (
                                            <tr key={s.id}>
                                                <td>{s.start_date || '–'}</td>
                                                <td>{s.end_date || '–'}</td>
                                                <td className="ed-td-r"><strong>{s.number_days ?? '–'}</strong></td>
                                            </tr>
                                        ))
                                    }
                                </tbody>
                                <tfoot>
                                    <tr className="ed-tfoot-row">
                                        <td colSpan={2}><strong>Gesamt</strong></td>
                                        <td className="ed-td-r"><strong>{totalSickDays} Tage</strong></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </section>

            </div>

            <section className="ed-section">
                <div className="ed-section-header">
                    <h3 className="ed-section-title">Arbeitseinträge {year}</h3>
                    <div className="ed-entries-meta">
                        <span className="ed-badge ed-badge--closed">{closedCount} abgeschlossen</span>
                        {openCount > 0 && (
                            <span className="ed-badge ed-badge--open">{openCount} offen</span>
                        )}
                    </div>
                </div>
                {loadingEn ? (
                    <p className="ed-loading">Lade Einträge…</p>
                ) : entries.length === 0 ? (
                    <p className="ed-empty">Keine Einträge für {year}.</p>
                ) : (
                    <div className="ed-card">
                        <div className="ed-scroll-wrap">
                            <table className="ed-table ed-entries-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: 20 }} />
                                        <th>Datum</th>
                                        <th>Start</th>
                                        <th>Ende</th>
                                        <th className="ed-th-r">Arbeitszeit</th>
                                        <th className="ed-th-r">Pause</th>
                                        <th className="ed-th-r">Fahrt</th>
                                        <th className="ed-th-r">Buchungen</th>
                                        <th>Überstunden</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {entries.map(r => {
                                        const rowAllocs  = allocByRecord[r.id] || [];
                                        const isExpanded = expandedRecordId === r.id;
                                        return (
                                            <React.Fragment key={r.id}>
                                                <tr
                                                    className={`ed-entry-row ${isExpanded ? 'ed-entry-row--open' : ''} ${rowAllocs.length > 0 ? 'ed-entry-row--clickable' : ''}`}
                                                    onClick={() => rowAllocs.length > 0 && setExpandedRecordId(isExpanded ? null : r.id)}
                                                >
                                                    <td className="ed-chevron-cell">
                                                        {rowAllocs.length > 0 && (
                                                            <span className={`ed-chevron ${isExpanded ? 'ed-chevron--open' : ''}`}>▸</span>
                                                        )}
                                                    </td>
                                                    <td className="ed-date-cell">{r.date}</td>
                                                    <td>{r.start_time || '–'}</td>
                                                    <td>{r.end_time || '–'}</td>
                                                    <td className="ed-td-r"><strong>{r.net_work_time}</strong></td>
                                                    <td className="ed-td-r">{r.break_minutes ? `${r.break_minutes}m` : '–'}</td>
                                                    <td className="ed-td-r">{r.travel_minutes ? `${r.travel_minutes}m` : '–'}</td>
                                                    <td className="ed-td-r">
                                                        {rowAllocs.length > 0
                                                            ? <span className="ed-alloc-count">{rowAllocs.length}</span>
                                                            : <span className="ed-muted">–</span>
                                                        }
                                                    </td>
                                                    <td>
                                                        {(() => {
                                                            const ot = otByRecord[r.id];
                                                            if (!ot || !ot.overtime_minutes) return <span className="ed-muted">–</span>;
                                                            const label = otLabel[ot.approval_status] || 'Ausstehend';
                                                            const reason = ot.approval_status === 'rejected' && ot.rejection_reason
                                                                ? ` – ${ot.rejection_reason}` : '';
                                                            return (
                                                                <span
                                                                    className={`ed-appr-badge ed-appr-badge--${ot.approval_status}`}
                                                                    title={`Überstunden ${label}${reason}`}
                                                                >
                                                                    +{fmtMins(ot.overtime_minutes)} · {label}
                                                                </span>
                                                            );
                                                        })()}
                                                    </td>
                                                    <td>
                                                        <span className={`ed-status ${r.status === 'closed' ? 'ed-status--closed' : 'ed-status--open'}`}>
                                                            {r.status === 'closed' ? 'Abgeschlossen' : 'Offen'}
                                                        </span>
                                                    </td>
                                                </tr>

                                                {isExpanded && (
                                                    <tr className="ed-alloc-row">
                                                        <td colSpan={10} className="ed-alloc-cell">
                                                            <table className="ed-alloc-table">
                                                                <thead>
                                                                    <tr>
                                                                        <th>Projekt</th>
                                                                        <th>Ticket</th>
                                                                        <th className="ed-th-r">Zeit</th>
                                                                        <th>Einsatzort</th>
                                                                        <th>Aktivität</th>
                                                                        <th>Kommentar</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {rowAllocs.map(a => (
                                                                        <tr key={a.id}>
                                                                            <td className="ed-alloc-project">{a.project_name}</td>
                                                                            <td>{a.ticket_title || '–'}</td>
                                                                            <td className="ed-td-r"><strong>{fmtMins(a.allocated_minutes)}</strong></td>
                                                                            <td>{a.location || '–'}</td>
                                                                            <td>{a.activity || '–'}</td>
                                                                            <td className="ed-muted">{a.comment || '–'}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </section>

        </div>
    );
}
